import type { ComponentPropsWithRef, ReactNode } from 'react'
import { useId } from 'react'

import { cn } from '@/shared/lib'
import { FieldOutline } from '@/shared/ui/field-outline'
import { FloatingLabel } from '@/shared/ui/floating-label'

type FloatingTextareaProps = Omit<ComponentPropsWithRef<'textarea'>, 'placeholder'> & {
  label: string
  hint?: ReactNode
  className?: string
}

/**
 * The Verdant textarea: 22/12/8 padding to clear the label, radius 10, sunken,
 * resizable vertically only. Like the select, the label is always at the top —
 * a multi-line field is tall enough that a centred label would read as content.
 */
export function FloatingTextarea({
  label,
  hint,
  className,
  rows = 3,
  id,
  ...props
}: FloatingTextareaProps) {
  const generatedId = useId()
  const fieldId = id ?? generatedId
  const hintId = `${fieldId}-hint`

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <div className="group/field relative">
        <textarea
          id={fieldId}
          rows={rows}
          aria-describedby={hint === undefined ? undefined : hintId}
          className="w-full resize-y rounded-lg bg-surface-sunken px-3 py-2.5 text-body leading-[1.5] text-foreground transition-colors outline-none disabled:cursor-not-allowed disabled:bg-surface-1 disabled:text-fg-disabled aria-invalid:bg-destructive/8 aria-invalid:text-destructive-text"
          {...props}
        />
        <FieldOutline label={label} isRaised className="border-border-strong" />
        <FloatingLabel htmlFor={fieldId} isRaised>
          {label}
        </FloatingLabel>
      </div>

      {hint !== undefined && (
        <div id={hintId} className="text-caption text-muted-foreground">
          {hint}
        </div>
      )}
    </div>
  )
}
